/**
 * Core bounty domain model mirroring the backend API.
 *
 * Defines the Bounty interface, BountyStatus/MatchType unions, title and
 * description length limits, and helpers that describe match types for the
 * UI (tooltips/labels). Consumed widely: api/bounty.ts, api/userBounty.ts,
 * api/userPursuit.ts, api/admin.ts, bounty pages, and shared item components.
 */
import { Category, Sex } from "@/types/category";
import { CreatedUpdatedAt } from "@/types/createdUpdatedAt";

export interface Bounty extends CreatedUpdatedAt {
    id?: number;
    status?: BountyStatus;
    title: string;
    description: string;
    category: Category;
    categoryType: string | null;
    sex: Sex | null;
    minPrice: number;
    maxPrice: number;
    reward: number;
    s3ImageUrls: string[];
    porsuiUserId: number;
    type: MatchType;
}

export const BOUNTY_TITLE_MAX_LENGTH = 60;
export const BOUNTY_DESCRIPTION_MAX_LENGTH = 500;

export type BountyStatus = "pending" | "active" | "completed" | "cancelled" | "expired";

export type MatchType = "exact" | "similar" | "matching";

export const MatchTypes: readonly MatchType[] = ["exact", "similar", "matching"];

export type BountyTypeMessageDirection = "you" | "they";

export const bountyTypeDescription = (
    type: MatchType,
    direction: BountyTypeMessageDirection
): string => {
    switch (type) {
        case "exact":
            return `${direction} want the exact item in the photos/description`;
        case "similar":
            return `${direction} want a similar item to the one in the photos/description`;
        case "matching":
            return `${direction} want an item that matches/goes with the content in the photos, but the description is of the actual item`;
    }
};

export const bountyTypeTooltipMessage = (type: BountyTypeMessageDirection): string => {
    return (
        `these values refer to how ${
            type === "you" ? "your" : "the"
        } photos and description will be used to match submissions\n\n` +
        `exact - ${bountyTypeDescription("exact", type)}\n\n` +
        `similar - ${bountyTypeDescription("similar", type)}\n\n` +
        `matching - ${bountyTypeDescription("matching", type)}`
    );
};
